import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Database, Radio, Film } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusPill, inputCls } from "@/components/Peep";
import { IntegrationHealthPanel } from "@/components/IntegrationHealth";

function Row({ k, v, testid }) {
  return (
    <div className="flex justify-between gap-3 border-t border-white/5 py-1.5 text-xs">
      <span className="text-[#a7a0af]">{k}</span>
      <span data-testid={testid} className="text-right">{v}</span>
    </div>
  );
}

function SecretForm({ placeholder, onSave, testid, withKey }) {
  const [v, setV] = useState("");
  const [k, setK] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    if (await onSave(v.trim(), k.trim())) { setV(""); setK(""); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-2">
      {withKey && (
        <input type="text" autoComplete="off" autoCapitalize="none" spellCheck={false} value={k} onChange={(e) => setK(e.target.value)}
          data-testid={`${testid}-key-input`} placeholder={withKey} className={inputCls} />
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
      <div className="relative flex-1">
        <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
        <input type="password" autoComplete="off" autoCapitalize="none" spellCheck={false} value={v} onChange={(e) => setV(e.target.value)}
          data-testid={`${testid}-input`} placeholder={placeholder} className={`${inputCls} pl-9`} />
      </div>
      <button disabled={busy || v.length < 20} data-testid={`${testid}-save`} className="btn-brand rounded-xl px-5 py-2.5 text-sm font-semibold">{busy ? "Verifying…" : "Verify & save"}</button>
      </div>
    </form>
  );
}

function CloudflareForm({ onSaved }) {
  const [acc, setAcc] = useState("");
  const [tok, setTok] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/integrations/cloudflare", { account_id: acc.trim(), api_token: tok.trim() });
      setTok("");
      toast.success("Cloudflare Stream connected — token stored encrypted on the server");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-2">
      <input type="text" autoComplete="off" autoCapitalize="none" spellCheck={false} value={acc} onChange={(e) => setAcc(e.target.value)}
        data-testid="cloudflare-account-input" placeholder="Cloudflare Account ID (32 characters)" className={inputCls} />
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
          <input type="password" autoComplete="off" autoCapitalize="none" spellCheck={false} value={tok} onChange={(e) => setTok(e.target.value)}
            data-testid="cloudflare-token-input" placeholder="Paste PEEPING_Stream_Integration API token" className={`${inputCls} pl-9`} />
        </div>
        <button disabled={busy || tok.length < 20 || acc.trim().length !== 32} data-testid="cloudflare-save" className="btn-brand rounded-xl px-5 py-2.5 text-sm font-semibold">{busy ? "Verifying…" : "Verify & save"}</button>
      </div>
    </form>
  );
}

const CF_DIAG = { missing_credentials: "Missing credentials — no API token / Account ID is configured on the server", credentials_rejected: "Credentials present but rejected by Cloudflare", connected: "Connected ✓" };

export default function AdminIntegrations() {
  const [d, setD] = useState(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/integrations").then((r) => setD(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/integrations/supabase", { secret_key: key });
      setKey("");
      toast.success("Supabase connected — key stored encrypted on the server");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const saveStream = async (api_secret, api_key) => {
    try {
      await api.put("/admin/integrations/getstream", api_key ? { api_secret, api_key } : { api_secret });
      toast.success("GetStream connected — private call type configured");
      load();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    }
  };
  const s = d?.supabase;
  const g = d?.getstream;
  const cf = d?.cloudflare_stream;
  return (
    <div data-testid="admin-integrations" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Integrations</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">Secrets entered here go straight to the server, are stored encrypted, and are never shown again.</p>
      </div>
      <IntegrationHealthPanel onChecked={load} />
      <Panel title="Supabase Storage" action={<StatusPill status={!d ? "checking" : s?.check?.ok ? "active" : s?.key_configured ? "failed" : "not configured"} testid="supabase-status" />}>
        {s && (
          <>
            <Row k="Project URL" v={s.url} />
            <Row k="Bucket" v={s.bucket} />
            <Row k="Secret key" v={s.key_configured ? `Configured ${s.key_hint} (${s.key_source})` : "Not set"} testid="supabase-key-state" />
            <Row k="New uploads go to" v={s.uploads_using} testid="supabase-uploads-using" />
            <Row k="Media files" v={`${s.assets.supabase} in Supabase · ${s.assets.emergent} in existing storage (not migrated)`} />
            {s.check && <Row k="Bucket check" v={s.check.ok ? `Private ✓${s.check.file_size_limit ? ` · limit ${Math.round(s.check.file_size_limit / 1048576)}MB` : ""}` : s.check.error} testid="supabase-check" />}
          </>
        )}
        <form onSubmit={save} className="mt-4 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <KeyRound size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a7a0af]" />
            <input type="password" autoComplete="off" autoCapitalize="none" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)}
              data-testid="supabase-key-input" placeholder={s?.key_configured ? "Replace secret key (sb_secret_…)" : "Paste secret key (sb_secret_…)"} className={`${inputCls} pl-9`} />
          </div>
          <button disabled={busy || key.length < 20} data-testid="supabase-key-save" className="btn-brand rounded-xl px-5 py-2.5 text-sm font-semibold">{busy ? "Verifying…" : "Verify & save"}</button>
        </form>
      </Panel>
      <div className="grid gap-3">
        <Panel title="GetStream Video & Chat (live)" action={<StatusPill status={!d ? "checking" : g?.check?.ok ? "active" : g?.secret_configured ? "failed" : "not configured"} testid="getstream-status" />}>
          {g && (
            <>
              <Row k="App ID" v={g.app_id} />
              <Row k="API key (public)" v={g.api_key} />
              <Row k="API secret" v={g.secret_configured ? `Configured ${g.secret_hint} (${g.secret_source})` : "Not set"} testid="getstream-secret-state" />
              <Row k="Private call type" v={g.call_type} />
              <Row k="Live now" v={g.live_now} />
              {g.check && <Row k="Connection" v={g.check.ok ? "Connected ✓" : g.check.error} testid="getstream-check" />}
            </>
          )}
          <SecretForm placeholder={g?.secret_configured ? "Replace API secret" : "Paste GetStream API secret"} onSave={saveStream} testid="getstream-secret"
            withKey={`New API key (optional — leave blank to keep ${g?.api_key || "current key"})`} />
          <p className="mt-2 flex gap-2 text-[0.65rem] text-[#6f6878]"><Radio size={12} className="shrink-0" /> GetStream Dashboard → PEEPING → Overview → App Access Keys. Use the copy icon next to the secret, or tap “Add new” to create a fresh key + secret pair and paste both here. The current pair stays active until the new one is verified.</p>
        </Panel>
        <Panel title="Cloudflare Stream (video)" action={<StatusPill status={!d ? "checking" : cf?.connected ? "active" : cf?.token_configured ? "failed" : "not configured"} testid="cloudflare-status" />}>
          {cf && (
            <>
              <Row k="Diagnosis" v={CF_DIAG[cf.diagnosis]} testid="cloudflare-diagnosis" />
              <Row k="Account ID" v={cf.account_id || "Not set"} testid="cloudflare-account" />
              <Row k="API token" v={cf.token_configured ? `Configured ${cf.token_hint} (${cf.token_source})` : "Not set"} testid="cloudflare-token-state" />
              {cf.check && <Row k="Token" v={cf.check.token_valid ? `Valid (${cf.check.token_status})` : cf.check.token_status || "Invalid"} />}
              {cf.check && <Row k="Stream access" v={cf.check.stream_access ? `Granted · ${cf.check.video_count ?? 0} videos in account` : cf.check.error} testid="cloudflare-check" />}
              {cf.check?.minutes_limit != null && <Row k="Storage minutes" v={`${Math.round(cf.check.minutes_used || 0)} / ${cf.check.minutes_limit}`} />}
              <Row k="Limits" v={`${cf.max_video_mb / 1024} GB · ${cf.max_minutes} min per video`} />
              <Row k="Videos uploaded via PEEPING" v={cf.videos} />
            </>
          )}
          <CloudflareForm onSaved={load} />
          <p className="mt-2 flex gap-2 text-[0.65rem] text-[#6f6878]"><Film size={12} className="shrink-0" /> Cloudflare Dashboard → your PEEPING account → Account ID is on the account home (right side). Token: Manage Account → Account API Tokens (or My Profile → API Tokens) → PEEPING_Stream_Integration → Roll to reveal a new value. Needs Stream: Edit. Videos stay private (signed playback only).</p>
        </Panel>
      </div>
      <p className="flex items-center gap-2 text-xs text-[#6f6878]"><Database size={13} /> Authentication, memberships and payments remain in MongoDB.</p>
    </div>
  );
}
